import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { AppState } from '../app.state';
import { catchError, debounceTime, exhaustMap, mergeMap, of, share, switchMap } from 'rxjs';
import { withLatestFrom } from 'rxjs/operators';
import { SharedActions } from '@states/shared/shared.action-types';
import { PermissionGroupsActions } from '@states/group/group.action-types';
import { PermissionGroupService } from '../../services/permission-group.service';
import { PermissionActions } from '@states/permissions/permissions.action-types';
import { OrganizationService } from '../../development/organization.service';
import { UtilsService } from '../../edge/utils.service';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import _ from 'lodash';
import { OrganizationUsersActions } from '@states/organization-users/organization-users.action-types';
import { Dictionary } from '@ngrx/entity/src/models';
import { PermissionGroupModels } from '@models/group.model';

@Injectable()
export class PermissionGroupsEffect {
  public beforeGetPermissionGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PermissionGroupsActions.beforeGetPermissionGroups),
      exhaustMap(() => [PermissionGroupsActions.setIsLoading({ isLoading: true }), PermissionGroupsActions.getPermissionGroups()]),
    ),
  );

  public getPermissionGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PermissionGroupsActions.getPermissionGroups),
      withLatestFrom(this.store$.pipe(select((state) => state.permissionGroupState))),
      switchMap(([, { filters, page, perPage, isLastPage }]) => {
        if (!isLastPage) {
          return this.permissionGroupService.findAll(filters, { page, perPage })
            .pipe(
              switchMap((res) => {
                return [PermissionGroupsActions.getPermissionGroupsSuccess({ permissionGroups: res.items }), PermissionGroupsActions.setIsLoading({ isLoading: false })];
              }),
              catchError((response) => {
                return [PermissionGroupsActions.getPermissionGroupsFail(), PermissionGroupsActions.setIsLoading({ isLoading: false })];
              }),
            );
        } else {
          return of(PermissionGroupsActions.setIsLoading({ isLoading: false }));
        }
      }),
      share(),
    ),
  );

  public savePermissionGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PermissionGroupsActions.savePermissionGroup),
      withLatestFrom(this.store$.pipe(select((state) => state.permissionGroupState))),
      switchMap(([{ permissionGroup }]) => {
        const beforeSend = permissionGroup;
        if (beforeSend?._id) {
          return this.permissionGroupService.update(beforeSend)
            .pipe(
              switchMap((res) => {
                return [
                  PermissionGroupsActions.updatePermissionGroupSuccess({ permissionGroup: beforeSend }),
                ];
              }),
              catchError((response) => {
                return [SharedActions.showMessage({ error: 'Group update fail' })];
              }),
            );
        }
        return this.permissionGroupService.create(beforeSend)
          .pipe(
            switchMap((res) => {
              return [PermissionGroupsActions.savePermissionGroupSuccess({ permissionGroup: res })];
            }),
            catchError((response) => {
              return [PermissionGroupsActions.savePermissionGroupFail()];
            }),
          );
      }),
      share(),
    ),
  );

  public getSelectedPermissionGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PermissionGroupsActions.getSelectedPermissionGroup),
      switchMap(({ id }) => {
        return this.permissionGroupService.findOne(id)
          .pipe(
            switchMap((res) => {
              return [PermissionGroupsActions.setSelectedPermissionGroup({ selectedPermissionGroup: res })];
            }),
            catchError((response) => {
              return [PermissionGroupsActions.getSelectedPermissionGroupFail()];
            }),
          );
      }),
      share(),
    ),
  );

  public updatePermissionGroupPermission$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PermissionGroupsActions.updatePermissionGroup),
      withLatestFrom(this.store$.pipe(select((state) => state.permissionGroupState))),
      switchMap(([, { selectedPermissionGroup }]) => {
        const beforeSend = selectedPermissionGroup;
        return this.permissionGroupService.update(beforeSend)
          .pipe(
            switchMap((res) => {
              return [SharedActions.doNothing()];
            }),
            catchError((response: HttpErrorResponse) => {
              return [SharedActions.showMessage({ error: this.utilsService.errMessage(response) })];
            }),
          );
      }),
      share(),
    ),
  );

  public filterTrigger$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PermissionGroupsActions.setFilter),
      debounceTime(400),
      switchMap(() => [PermissionGroupsActions.resetEntities(), PermissionGroupsActions.beforeGetPermissionGroups()]),
    ),
  );

  public autoUpdateTrigger = createEffect(() =>
    this.actions$.pipe(
      ofType(
        PermissionGroupsActions.changePermission,
        PermissionGroupsActions.selectPermissionsByEntity,
        PermissionGroupsActions.selectAllPermissions,
        PermissionGroupsActions.makeInherited,
      ),
      debounceTime(400),
      switchMap(() => [PermissionGroupsActions.updatePermissionGroup()]),
    ),
  );

  public changeUsersTriggered = createEffect(() =>
    this.actions$.pipe(
      ofType(PermissionGroupsActions.changeUsers),
      withLatestFrom(this.store$.pipe(select((state) => state.permissionGroupState))),
      debounceTime(400),
      switchMap(([{ users }, { selectedPermissionGroup }]) =>
        this.permissionGroupService.updateUsers(selectedPermissionGroup._id, users)
          .pipe(
            switchMap((res) => {
              return [
                PermissionGroupsActions.changeUsersSuccess({ users }),
                SharedActions.showMessage({ success: 'Users have been updated' }),
              ];
            }),
            catchError((response) => {
              return [SharedActions.showMessage({ error: response?.error?.message })];
            }),
          ),
      ),
    ),
  );


  public removeGroupUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PermissionGroupsActions.removeGroupUser),
      withLatestFrom(this.store$.pipe(select((state) => state.permissionGroupState))),
      mergeMap(([{ userId }, { selectedPermissionGroup }]) => {
          const groupUsers = { ...selectedPermissionGroup.users };
          delete groupUsers[userId];
          return this.permissionGroupService.updateUsers(selectedPermissionGroup._id, groupUsers)
            .pipe(
              mergeMap((res) => {
                return [
                  PermissionGroupsActions.changeUsersSuccess({ users: groupUsers }),
                  SharedActions.showMessage({ success: 'Users have been updated' }),
                ];
              }),
              catchError((response) => {
                return [SharedActions.showMessage({ error: response?.error?.message })];
              }),
            );
        },
      ),
    ),
  );

  public removePermissionGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PermissionGroupsActions.removePermissionGroup),
      switchMap(({ id }) => {
        return this.permissionGroupService.remove(id)
          .pipe(
            switchMap((res) => {
              return [PermissionGroupsActions.removePermissionGroupSuccess({ id })];
            }),
            catchError((response) => {
              return [PermissionGroupsActions.removePermissionGroupFail({ id })];
            }),
          );
      }),
      share(),
    ),
  );

  public getPermissions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PermissionActions.getPermissions),
      switchMap(() => {
        return this.permissionGroupService.getUserPermissions()
          .pipe(
            switchMap((res) => {
              return [PermissionActions.getPermissionsSuccess(res)];
            }),
            catchError((response) => {
              return [PermissionActions.getPermissionsFail()];
            }),
          );
      }),
      share(),
    ),
  );

  public getSelectedUserPermissions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PermissionGroupsActions.getSelectedUserPermissions),
      switchMap(({ userId }) => {
        return this.permissionGroupService.getUserPermissionsById(userId)
          .pipe(
            switchMap((res) => {
              return [PermissionGroupsActions.getSelectedUserPermissionsSuccess({
                selectedUserPermissions: res,
                user: res.user,
              })];
            }),
            catchError((response) => {
              return [PermissionGroupsActions.getSelectedUserPermissionsFail()];
            }),
          );
      }),
      share(),
    ),
  );

  public getSelectedOrganizationPermissions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PermissionGroupsActions.getSelectedOrganizationPermissions),
      switchMap(({ orgId }) => {
        return this.permissionGroupService.getOrganizationPermissionsById(orgId)
          .pipe(
            switchMap((res) => {
              return [
                PermissionGroupsActions.getSelectedOrganizationPermissionsSuccess({
                  selectedUserPermissions: res,
                  organization: res.organization,
                }),
              ];
            }),
            catchError((response) => {
              return [PermissionGroupsActions.getSelectedOrganizationPermissionsFail()];
            }),
          );
      }),
      share(),
    ),
  );


  public getOrganizationUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PermissionGroupsActions.getOrganizationUsers),
      switchMap(() => {
        return this.organizationService.getActiveOrganizationMembers()
          .pipe(
            switchMap((res) => {
              return [
                PermissionGroupsActions.getOrganizationUsersSuccess({ users: res.users }),
                PermissionGroupsActions.getInvitedOrganizationSuccess({ organizations: res.invitedOrganizations }),
              ];
            }),
            catchError((response) => {
              const message = this.utilsService.errMessage(response);
              return [SharedActions.consoleMessage({ error: message })];
            }),
          );
      }),
      share(),
    ),
  );


  public updateUserGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PermissionGroupsActions.updateUserGroups),
      switchMap(({ groupIds, userId }) => {
        return this.permissionGroupService.updateUserGroups(groupIds, userId)
          .pipe(
            switchMap((res) => {
              return [
                PermissionGroupsActions.removeSavingId({ id: userId }),
                SharedActions.showMessage({ success: this.translateService.instant('TOASTS.PERMISSION_UPDATE_SUCCESS') }),
              ];
            }),
            catchError((response) => {
              return [
                PermissionGroupsActions.removeSavingId({ id: userId }),
                SharedActions.showMessage({ error: this.translateService.instant('TOASTS.PERMISSION_UPDATE_FAILED') }),
              ];
            }),
          );
      }),
      share(),
    ),
  );

  public updateOrganizationPermissionGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PermissionGroupsActions.updateOrganizationPermissionGroups),
      switchMap(({ groupIds, orgId }) => {
        return this.permissionGroupService.updateOrganizationGroups(groupIds, orgId)
          .pipe(
            switchMap((res) => {
              return [
                PermissionGroupsActions.updateOrganizationPermissionGroupsSuccess({ groupIds, orgId }),
                PermissionGroupsActions.removeSavingId({ id: orgId }),
                SharedActions.showMessage({ success: this.translateService.instant('TOASTS.PERMISSION_UPDATE_SUCCESS') })];
            }),
            catchError((response) => {
              const error = this.utilsService.errMessage(response);
              return [
                PermissionGroupsActions.removeSavingId({ id: orgId }),
                SharedActions.showMessage({ error: this.translateService.instant('TOASTS.REMOVE_INVITED_ORGANIZATION_FAILED') }),
                SharedActions.consoleMessage({ error }),
              ];
            }),
          );
      }),
      share(),
    ),
  );

  public updateOrganizationPermissionGroupsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PermissionGroupsActions.updateOrganizationPermissionGroupsSuccess),
      withLatestFrom(this.store$.pipe(select((state) => state.organizationUsersState))),
      switchMap(([{ groupIds, orgId }, { permissionGroups }]) => {
        const permissionGroupsArray = Object.keys(permissionGroups);
        const result: Dictionary<PermissionGroupModels.PermissionGroupDocumentBase> = {};
        permissionGroupsArray.forEach(groupId => {
          const group = permissionGroups[groupId];
          if (groupIds.includes(groupId)) {
            result[groupId] = {
              ...group,
              organizations: {
                ...group.organizations,
                [orgId]: {},
              },
            };
          } else {
            const groupCopy = _.cloneDeep(group);
            delete groupCopy.organizations[orgId];
            result[groupId] = groupCopy;
          }
        });
        return [
          OrganizationUsersActions.setOrganizationInvitedOrgPermissions({ toUpdate: result }),
        ];
      }),
      share(),
    ),
  );

  constructor(private actions$: Actions,
              private store$: Store<AppState>,
              private permissionGroupService: PermissionGroupService,
              private organizationService: OrganizationService,
              private utilsService: UtilsService,
              private translateService: TranslateService) {
  }

}
